// Models
import {
  CreateChargeIntentRequestBody,
  PaymentDetailsPayPalCommerceRequest,
  PaymentDetailsACHRequest,
  PaymentMethod,
  PaymentProcessor,
  TransactionType,
  PaymentDetailsCCRequest,
  DonateResponse,
} from 'models/ChargeIntent'

import _ from 'lodash'

import {
  ACHFinalizationData,
  PayPalBraintreeFinalizationData,
  PlaidFinalizationData,
  StripeFinalizationData,
  TokenExFinalizationData,
} from 'models/PaymentDetails'
import { Transaction } from 'models/Transaction'

// Redux services
import { createChargeIntent } from 'redux/services/chargeIntent'
import { donate, recurring } from 'redux/services/donate'

// Local
import { RootState, TypedDispatch } from 'redux/store'
import getStripePromise from 'utils/functions/getStripePromise'
import handleStripeError from 'utils/functions/handleStripeError'
import { FinalizeOrderArgs } from '../types'

import moment from 'moment'

const finalizePayment = async (
  store: RootState,
  dispatch: TypedDispatch<RootState>,
  transaction: Transaction,
  args: FinalizeOrderArgs,
): Promise<DonateResponse | undefined> => {
  const paymentDetails = store.order.paymentDetails

  const partialBody = {
    currency: args.currency,
    amount: store.order.paymentDetails.amount,
    firstName: store.order.billingDetails.firstName,
    lastName: store.order.billingDetails.lastName,
    address1: store.order.billingDetails.address.line1,
    address2: store.order.billingDetails.address.line2,
    city: store.order.billingDetails.address.city,
    state: store.order.billingDetails.address.state,
    country: store.order.billingDetails.address.country,
    zip: store.order.billingDetails.address.postalCode ?? '11111',
    email: store.order.billingDetails.email,
    referenceId: transaction.id.toString(),
    reCaptchaToken: args.reCaptchaToken,
    metaData: {
      employer_match: {
        employer_id: String(store.order.donationOptions.employerMatchCompanyId),
        employer_name: store.order.donationOptions.employerMatchCompanyName,
        employer_search_text: store.order.donationOptions.employerMatchCompanyName,
      },
    },
  }

  const isRecurring = !!args?.frequency && args.frequency.toLowerCase() != 'one-time'

  let donateResponse: DonateResponse | undefined

  switch (paymentDetails.processor) {
    case PaymentProcessor.STRIPE: {
      if (paymentDetails.type === PaymentMethod.Plaid) {
        // convert paymentDetails.method from PlaidFinalizationData to StripeFinalizationData
        const plaidPaymentDetails = paymentDetails.method as PlaidFinalizationData

        const newPaymentDetails = {
          accountId: plaidPaymentDetails.accountId,
          source: plaidPaymentDetails.publicToken,
        }

        const body = Object.assign(
          {
            type: TransactionType.Donation,

            paymentMethod: PaymentMethod.Stripe,
            paymentDetails: newPaymentDetails,
          },
          partialBody,
        )

        if (isRecurring) {
          const recurringBody = Object.assign(
            {
              recurringUnit: args.frequency?.toUpperCase(),
              recurUntil: args.recurringEndDate,
              recurringStartDate: moment.utc().format('YYYY-MM-DD'),
              recurringTimeZone: 'Etc/UTC',
            },
            body,
          )

          donateResponse = await dispatch(
            recurring.initiate({
              campaignId: args.cid,
              paymentMethod: paymentDetails.type,
              body: recurringBody,
            }),
          ).unwrap()
        } else {
          donateResponse = await dispatch(
            donate.initiate({ campaignId: args.cid, paymentMethod: paymentDetails.type, body }),
          ).unwrap()
        }

        const { errors, message } = donateResponse

        if (errors) {
          throw new Error(message)
        }

        break
      }

      //If not Plaid, then it's Stripe CC or ACH account and routing
      const paymentMethod =
        paymentDetails.type === PaymentMethod.ACH
          ? (paymentDetails.method as ACHFinalizationData)
          : (paymentDetails.method as StripeFinalizationData)

      const body: CreateChargeIntentRequestBody = Object.assign(
        {
          type: TransactionType.Donation,
          paymentMethod:
            paymentDetails.type === PaymentMethod.ACH ? PaymentMethod.ACH : PaymentMethod.Stripe,
          paymentDetails:
            paymentDetails.type === PaymentMethod.ACH
              ? (paymentMethod as PaymentDetailsACHRequest)
              : {
                  source: _.get(paymentMethod, 'id'),
                },
        },
        partialBody,
      )

      if (isRecurring) {
        body.recurringDetails = {
          recurringUnit: args.frequency?.toUpperCase(),
          recurUntil: args.recurringEndDate,
          recurringStartDate: moment.utc().format('YYYY-MM-DD'),
          recurringTimeZone: 'Etc/UTC',
        }
      }

      const createChargeIntentResponse = await dispatch(
        createChargeIntent.initiate({
          campaignId: args.cid,
          paymentMethod:
            paymentDetails.type === PaymentMethod.ACH ? PaymentMethod.ACH : PaymentMethod.Stripe,
          body,
        }),
      ).unwrap()

      const stripe = await getStripePromise(dispatch)

      // Sanity check on stripe
      if (!stripe) throw new Error('Could not finalize payment. Unable to initialize Stripe.')

      if ('errors' in createChargeIntentResponse) {
        throw new Error(createChargeIntentResponse.statusText)
      }

      const response = await stripe.confirmPayment({
        clientSecret: createChargeIntentResponse.chargeIntent.client_secret,
        redirect: 'if_required',
      })

      if (response.error) {
        const errorMessage = handleStripeError(response.error)
        throw new Error(errorMessage)
      }

      break
    }

    case PaymentProcessor.PAYPAL_COMMERCE: {
      const body = Object.assign(
        {
          type: TransactionType.Donation,
          paymentMethod: paymentDetails.type, //PPC or Venmo
          paymentDetails: paymentDetails.method as PaymentDetailsPayPalCommerceRequest, // 'orderId' XOR 'agreementId'
        },
        partialBody,
      )

      if (isRecurring) {
        const recurringBody = Object.assign(
          {
            recurringUnit: args.frequency?.toUpperCase(),
            recurUntil: args.recurringEndDate,
            recurringStartDate: moment.utc().format('YYYY-MM-DD'),
            recurringTimeZone: 'Etc/UTC',
          },
          body,
        )

        donateResponse = await dispatch(
          recurring.initiate({
            campaignId: args.cid,
            paymentMethod: paymentDetails.type,
            body: recurringBody,
          }),
        ).unwrap()
      } else {
        donateResponse = await dispatch(
          donate.initiate({ campaignId: args.cid, paymentMethod: paymentDetails.type, body }),
        ).unwrap()
      }

      const { errors, message } = donateResponse

      if (errors) {
        throw new Error(message)
      }

      break
    }

    case PaymentProcessor.WEPAY:
    case PaymentProcessor.BRAINTREE:
    // eslint-disable-next-line padding-line-between-statements
    case PaymentProcessor.AUTHORIZENET: {
      const body = Object.assign(
        {
          type: TransactionType.Donation,
          paymentMethod: paymentDetails.type,
          paymentDetails: {},
        },
        partialBody,
      )

      if (
        paymentDetails.processor === PaymentProcessor.BRAINTREE &&
        paymentDetails.type === PaymentMethod.PayPal
      ) {
        body.paymentDetails = paymentDetails.method as PayPalBraintreeFinalizationData
      } else {
        const ccPaymentDetails = paymentDetails.method as TokenExFinalizationData

        body.paymentDetails = {
          token: ccPaymentDetails.token,
          cvv: ccPaymentDetails.cvv,
          expirationMonth: ccPaymentDetails.expirationMonth.toString(),
          expirationYear: ccPaymentDetails.expirationYear.toString(),
        } as PaymentDetailsCCRequest
      }

      if (isRecurring) {
        const recurringBody = Object.assign(
          {
            recurringUnit: args.frequency?.toUpperCase(),
            recurUntil: args.recurringEndDate,
            recurringStartDate: moment.utc().format('YYYY-MM-DD'),
            recurringTimeZone: 'Etc/UTC',
          },
          body,
        )

        donateResponse = await dispatch(
          recurring.initiate({
            campaignId: args.cid,
            paymentMethod: paymentDetails.type,
            body: recurringBody,
          }),
        ).unwrap()
      } else {
        donateResponse = await dispatch(
          donate.initiate({ campaignId: args.cid, paymentMethod: paymentDetails.type, body }),
        ).unwrap()
      }

      const { errors, message } = donateResponse

      if (errors) {
        throw new Error(message)
      }

      break
    }

    default:
      throw new Error(`Unsupported payment method / processor: ${paymentDetails.processor}`)
  }

  return donateResponse
}

export default finalizePayment
